import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import {
  getClaimFormFn,
  requestOrganizationAccessFn,
} from "~/lib/functions/cco-representatives";
import { requireAuth } from "~/lib/functions/require-auth";
import {
  MAX_ACCESS_MESSAGE_LENGTH,
  MAX_ROLE_TITLE_LENGTH,
  type ClaimState,
} from "~/lib/shared/cco-representatives";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { UserFacingError } from "~/lib/shared/user-facing-error";

/**
 * "Claim this organization": a signed-in person asks DCM to make them a
 * representative. A signed-out visitor signs in or creates an account first
 * and comes back here.
 */
export const Route = createFileRoute("/_public/organizations/$slug/claim")({
  staticData: { title: "Claim this organization" },
  beforeLoad: async ({ location }) => {
    await requireAuth(location);
  },
  loader: async ({ params }) => {
    const form = await getClaimFormFn({ data: { slug: params.slug } });
    if (!form) throw notFound();
    return { title: `Claim ${form.organization.name}`, form };
  },
  head: () =>
    createNoIndexMeta(
      "Claim this organization – Dream Coach Match",
      "Ask Dream Coach Match to make you a representative of your organization.",
    ),
  component: ClaimOrganizationPage,
});

function ClaimOrganizationPage(): React.JSX.Element {
  const { form } = Route.useLoaderData();
  const [state, setState] = useState<ClaimState>(form.state);
  const { name, slug } = form.organization;

  return (
    <div className="mx-auto max-w-xl px-4 py-12 sm:py-16">
      <Card>
        <div className="space-y-2">
          <p className="eyebrow text-purple-700">Claim this organization</p>
          <h1 className="font-heading text-2xl font-medium tracking-tight text-gray-900">
            {name}
          </h1>
        </div>
        {state === "none" ? (
          <ClaimForm
            name={name}
            slug={slug}
            email={form.email}
            onSent={setState}
          />
        ) : state === "pending" ? (
          <ClaimMessage
            title="Your request is pending"
            body={`We review each request and email you when it's decided. Once we approve it, you can confirm which coaches on Dream Coach Match are certified by ${name} or trained in its programs.`}
          >
            <Link
              to="/organizations/$slug"
              params={{ slug }}
              className="text-sm text-purple-700 hover:underline"
            >
              Back to {name}
            </Link>
          </ClaimMessage>
        ) : (
          <ClaimMessage
            title={`You already represent ${name}`}
            body="You'll find it under My organization."
          >
            <Link
              to="/my-organization/$slug"
              params={{ slug }}
              className="text-sm text-purple-700 hover:underline"
            >
              Open My organization
            </Link>
          </ClaimMessage>
        )}
      </Card>
    </div>
  );
}

function ClaimMessage({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="space-y-3">
      <p className="flex items-center gap-2 text-base font-medium text-gray-900">
        <CheckCircle2 className="size-5 text-emerald-600" aria-hidden="true" />
        {title}
      </p>
      <p className="text-text-secondary text-sm">{body}</p>
      {children}
    </div>
  );
}

function ClaimForm({
  name,
  slug,
  email,
  onSent,
}: {
  name: string;
  slug: string;
  email: string | null;
  onSent: (state: ClaimState) => void;
}): React.JSX.Element {
  const request = useMutation({
    mutationFn: (values: { roleTitle: string; message: string }) =>
      requestOrganizationAccessFn({
        data: {
          slug,
          roleTitle: values.roleTitle,
          message: values.message || undefined,
          authorized: true,
        },
      }),
    onSuccess: onSent,
  });

  const form = useForm({
    defaultValues: { roleTitle: "", message: "", authorized: false },
    onSubmit: ({ value }) => request.mutateAsync(value),
  });

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <p className="text-text-secondary text-sm">
        Once DCM approves your request, you can confirm which coaches on Dream
        Coach Match are certified by {name} or trained in its programs.
      </p>

      <form.Field
        name="roleTitle"
        validators={{
          onSubmit: ({ value }) =>
            value.trim() ? undefined : `Tell us your role at ${name}.`,
        }}
      >
        {(field) => (
          <div className="space-y-1.5">
            <Label htmlFor="claim-role">Your role at {name}</Label>
            <Input
              id="claim-role"
              value={field.state.value}
              onValueChange={field.handleChange}
              onBlur={field.handleBlur}
              maxLength={MAX_ROLE_TITLE_LENGTH}
              placeholder="Director of certification"
              error={field.state.meta.errors[0]}
            />
            {field.state.meta.errors[0] && (
              <p className="text-xs text-red-600">
                {field.state.meta.errors[0]}
              </p>
            )}
          </div>
        )}
      </form.Field>

      {email && (
        <div className="space-y-1.5">
          <Label htmlFor="claim-email">Signed in as</Label>
          <Input id="claim-email" value={email} readOnly disabled />
        </div>
      )}

      <form.Field name="message">
        {(field) => (
          <div className="space-y-1.5">
            <Label htmlFor="claim-message">
              Anything we should know{" "}
              <span className="text-text-tertiary font-normal">(optional)</span>
            </Label>
            <Textarea
              id="claim-message"
              rows={3}
              value={field.state.value}
              onValueChange={field.handleChange}
              onBlur={field.handleBlur}
              maxLength={MAX_ACCESS_MESSAGE_LENGTH}
              placeholder="How we can reach you to confirm"
            />
          </div>
        )}
      </form.Field>

      <form.Field
        name="authorized"
        validators={{
          onSubmit: ({ value }) =>
            value
              ? undefined
              : "Confirm that you may confirm credentials for the organization.",
        }}
      >
        {(field) => (
          <div className="space-y-1.5">
            <Checkbox
              id="claim-authorized"
              checked={field.state.value}
              onCheckedChange={(checked) =>
                field.handleChange(checked === true)
              }
            >
              I'm authorized to confirm credentials for {name}.
            </Checkbox>
            {field.state.meta.errors[0] && (
              <p className="text-xs text-red-600">
                {field.state.meta.errors[0]}
              </p>
            )}
          </div>
        )}
      </form.Field>

      {request.error && (
        <p className="text-sm text-red-600" role="alert">
          {request.error instanceof UserFacingError
            ? request.error.message
            : "We couldn't send your request. Please try again."}
        </p>
      )}

      <form.Subscribe selector={(s) => s.isSubmitting}>
        {(isSubmitting) => (
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={isSubmitting}
          >
            Send request
          </Button>
        )}
      </form.Subscribe>
      <p className="text-text-tertiary text-center text-xs">
        We review each request and email you when it's approved.
      </p>
    </form>
  );
}
